import React, { useCallback, useContext, useState } from "react";
import { type DayType, type MonthType } from "./types";

interface IPickADayContextValue {
  scrollPercent: number;
  day: DayType;
  month: MonthType;
  year: number;
}

interface IPickADayContext extends IPickADayContextValue {
  setScrollPercent: (percent: number) => void;
  setYear: (year: number) => void;
  setMonth: (month: MonthType) => void;
  setDay: (day: DayType) => void;
}

const today = new Date();

const defaultValue: IPickADayContextValue = {
  scrollPercent: 0,
  year: Number(today.getFullYear()),
  month: Number(today.getMonth()) as MonthType,
  day: Number(today.getDate()) as DayType,
};

const PickADayContext = React.createContext<IPickADayContext>(
  defaultValue as IPickADayContext,
);

interface PickADayContextProviderProps {
  children?: React.ReactNode;
}

export const PickADayContextProvider: React.FC<
  PickADayContextProviderProps
> = ({ children }) => {
  const [state, setState] = useState(defaultValue);

  const setScrollPercent = useCallback(
    (scrollPercent: number) => {
      setState((old) => ({ ...old, scrollPercent }));
    },
    [setState],
  );

  const setYear = useCallback(
    (year: number) => {
      setState((old) => ({ ...old, year }));
    },
    [setState],
  );

  const setMonth = useCallback(
    (month: MonthType) => {
      setState((old) => ({ ...old, month }));
    },
    [setState],
  );

  const setDay = useCallback(
    (day: DayType) => {
      setState((old) => ({ ...old, day }));
    },
    [setState],
  );

  return (
    <PickADayContext.Provider
      value={{
        ...state,
        setScrollPercent,
        setYear,
        setMonth,
        setDay,
      }}
    >
      {children}
    </PickADayContext.Provider>
  );
};

export const usePickADayContext = (): IPickADayContext => {
  return useContext<IPickADayContext>(PickADayContext);
};
